import useMediaQuery from '@mui/material/useMediaQuery'

/**
 * How a person enters a quantity.
 *
 * `keypad` puts the on-screen number pad in front of them. `field` lets them
 * type into the value itself and shows no pad.
 */
export type QuantityEntry = 'keypad' | 'field'

/**
 * Which one to offer.
 *
 * Decided from the pointer rather than asked, because the browser already
 * knows: `pointer: coarse` is the *primary* input being a finger or stylus, so
 * a tablet on a pick cart answers yes and a desktop answers no. A laptop with a
 * touchscreen answers no, which is right — it has a keyboard, and its owner
 * would rather type than poke at a pad.
 *
 * The media query is live, so a tablet that gains a trackpad switches without
 * a reload.
 *
 * Deliberately the only place this is decided. A stored per-device preference
 * belongs here too if detection is ever caught out — the case to watch is a
 * tablet docked with a keyboard, where the trackpad may report a fine pointer.
 * Nothing that calls this needs to change for that to be added, which is why
 * callers ask this hook rather than the media query.
 */
export function useQuantityEntry(): QuantityEntry {
	return useMediaQuery( '(pointer: coarse)' ) ? 'keypad' : 'field'
}
